import './field.scss'

import { FieldState, FormApi } from 'final-form';
import { set } from 'lodash'
import { defineComponent, PropType } from 'vue';

export interface FieldStatus {
    disabled: boolean,
    disabledMessage?: string
}

export const Field = defineComponent({
    props: {
        form: { type: Object as PropType<FormApi<any>>, required: true },
        label: {},
        name: { type: String, required: true },
        emitChangeEvents: { type: Boolean, default: false },
        // When set to true, the change event of the field will
        // automatically update the field value in the form state.
        // This is needed for fields nested in dynamic list, not to
        // loose previous unsaved modifications when an element of the list
        // is added/removed
        autoUpdate: { type: Boolean, default: false },
        getFieldStatus: { type: Function as PropType<(fieldName: string) => FieldStatus | undefined> },
        dataCy: {},
        readOnly: {},
    },
    template: require('./field.html'),
    data() {
        return {
            bootstrapState: null as boolean | null,
            errorMessage: undefined as string | undefined,
            fieldState: null as any as FieldState<any>,
            unsubscribe: undefined as (() => void) | undefined,
        }
    },
    computed: {
        isDisabled(): boolean {
            if(this.getFieldStatus) {
                const fieldStatus = this.getFieldStatus(this.name)
                if(fieldStatus) {
                    return fieldStatus.disabled
                }
            }

            return false
        },
        disabledMessage(): string | undefined {
            if(this.getFieldStatus) {
                const fieldStatus = this.getFieldStatus(this.name)
                if(fieldStatus) {
                    return fieldStatus.disabledMessage
                }
            }

            return undefined
        },
        sanitizedName(): string {
            return this.fieldState.name.replace(/[.[]/g, '-');
        },
    },
    created() {
        this.unsubscribe = this.form.registerField(
            this.name,
            state => {
                this.fieldState = state;
                this.bootstrapState = state.touched && !state.valid ? false : null;
                this.errorMessage = state.submitError || state.error
                if (this.emitChangeEvents) {
                    this.$parent?.$emit('field-value-changed', { name: this.name, value: state.value })
                }
            },
            {
                active: true,
                dirty: true,
                dirtySinceLastSubmit: true,
                error: true,
                initial: true,
                invalid: true,
                pristine: true,
                submitError: true,
                submitFailed: true,
                submitSucceeded: true,
                touched: true,
                valid: true,
                value: true,
                visited: true,
            },
        );
    },
    beforeUnmount() {
        requestAnimationFrame(() => this.unsubscribe?.());
    },
    methods: {
        onValueChange(value) {
            if (this.autoUpdate) {
                set(this.form.getState().values, this.name, value)
            }
            this.fieldState.change(value)
            if (this.emitChangeEvents) {
                this.$parent?.$emit('field-value-changed', { name: this.name, value })
            }
        },
    },
})
